import { useEffect, useState } from 'react';
import cx from 'classnames';
import type { SplatLoadingPhase } from '../../state/sceneLoading';
import styles from './SceneProgressBar.module.css';

const FETCH_PHASE_MAX = 70;
const FAKE_PHASE_MAX = 90;
const FADE_OUT_DELAY_MS = 500;

type SceneProgressBarProps = {
  visible: boolean;
  splatLoadedBytes: number;
  splatTotalBytes: number | null;
  splatPhase: SplatLoadingPhase;
};

const computeFetchPercent = (loaded: number, total: number | null): number => {
  if (!total || total <= 0) {
    return 0;
  }
  return Math.min((loaded / total) * FETCH_PHASE_MAX, FETCH_PHASE_MAX);
};

export const SceneProgressBar = ({
  visible,
  splatLoadedBytes,
  splatTotalBytes,
  splatPhase,
}: SceneProgressBarProps) => {
  const [displayPercent, setDisplayPercent] = useState(0);
  const [fadedOut, setFadedOut] = useState(false);

  useEffect(() => {
    // Splat mesh initialized, reveal animation about to start — jump to 100% and fade out.
    if (splatPhase === 'ready') {
      setDisplayPercent(100);

      const timer = setTimeout(() => {
        setFadedOut(true);
      }, FADE_OUT_DELAY_MS);

      return () => clearTimeout(timer);
    }

    // Fetch complete, GPU is parsing the mesh — hold at 90%.
    if (splatPhase === 'initializing') {
      setDisplayPercent(FAKE_PHASE_MAX);
      return;
    }

    // Downloading splat binary — real progress mapped to 0–70%.
    if (splatPhase === 'fetching') {
      const fetchPercent = computeFetchPercent(splatLoadedBytes, splatTotalBytes);
      setDisplayPercent(Math.round(fetchPercent));
    }
  }, [splatLoadedBytes, splatTotalBytes, splatPhase]);

  const isHidden = !visible || fadedOut;
  const scaleX = Math.min(displayPercent / 100, 1);

  return (
    <div className={cx(styles.wrapper, isHidden && styles.hidden)}>
      <div className={styles.container}>
        <div className={styles.track}>
          <div
            className={styles.fill}
            style={{ transform: `scaleX(${scaleX})` }}
          >
            <div className={styles.shimmer} />
          </div>
        </div>
        <span className={styles.label}>{displayPercent}%</span>
      </div>
    </div>
  );
};
